/**
 * Find Us Section Component
 * Combined map and list view with toggle and error handling
 */

import { useState, useCallback } from 'react';
import { MapIcon, List, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BranchMap, BranchMapSkeleton } from './branch-map';
import { BranchList, BranchListSkeleton } from './branch-list';
import type { Branch } from '../stores/branch-store';

interface FindUsSectionProps {
  branches: Branch[];
  isLoading?: boolean;
  error?: Error | null;
  className?: string;
}

type ViewMode = 'map' | 'list';

export function FindUsSection({ 
  branches, 
  isLoading = false, 
  error = null,
  className = '' 
}: FindUsSectionProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [hasMapError, setHasMapError] = useState(false);

  const handleMapError = useCallback(() => {
    setHasMapError(true);
    setViewMode('list');
  }, []);

  const toggleView = useCallback(() => {
    setViewMode(prev => prev === 'map' ? 'list' : 'map');
  }, []);

  // Show loading skeleton
  if (isLoading) {
    return (
      <section className={`py-8 border-t border-brand-100 ${className}`}>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-display font-bold text-brand-900">
            Find Our Locations
          </h2>
          <p className="text-brand-600 mt-1">
            Visit us at any of our branches
          </p>
        </div>
        
        <div className="mb-4">
          <BranchMapSkeleton className="h-[400px]" />
        </div>
        
        <BranchListSkeleton />
      </section>
    );
  }

  // Show error state
  if (error || branches.length === 0) {
    return (
      <section className={`py-8 border-t border-brand-100 ${className}`}>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-display font-bold text-brand-900">
            Find Our Locations
          </h2>
        </div>
        
        <div className="bg-brand-50 rounded-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-brand-400 mx-auto mb-3" />
          <p className="text-brand-600 mb-2">
            {error ? 'Unable to load branch locations.' : 'No branch locations available.'}
          </p>
          <p className="text-brand-500 text-sm">
            Please try again later or contact us directly.
          </p>
        </div>
      </section>
    );
  }

  // Filter branches with coordinates for map
  const branchesWithCoords = branches.filter(b => b.lat != null && b.lng != null);

  return (
    <section 
      className={`py-8 border-t border-brand-100 ${className}`}
      aria-labelledby="find-us-heading"
    >
      {/* Section Header */}
      <div className="text-center mb-6">
        <h2 
          id="find-us-heading"
          className="text-2xl font-display font-bold text-brand-900"
        >
          Find Our Locations
        </h2>
        <p className="text-brand-600 mt-1">
          Visit us at any of our branches
        </p>
      </div>

      {/* View Toggle */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex rounded-lg border border-brand-200 p-1 bg-white">
          <Button
            variant={viewMode === 'map' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('map')}
            className={viewMode === 'map' ? 'bg-brand-600 hover:bg-brand-700' : ''}
            disabled={hasMapError}
          >
            <MapIcon className="w-4 h-4 mr-1.5" />
            Map View
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className={viewMode === 'list' ? 'bg-brand-600 hover:bg-brand-700' : ''}
          >
            <List className="w-4 h-4 mr-1.5" />
            List View
          </Button>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <div className="mb-6">
          {hasMapError ? (
            <div className="bg-brand-50 rounded-lg p-6 text-center">
              <AlertCircle className="w-12 h-12 text-brand-400 mx-auto mb-3" />
              <p className="text-brand-600 mb-3">
                Unable to load the map. Please use the list view below.
              </p>
              <Button variant="outline" onClick={() => setViewMode('list')}>
                View as List
              </Button>
            </div>
          ) : branchesWithCoords.length > 0 ? (
            <BranchMap 
              branches={branches} 
              className="h-[300px] md:h-[400px]"
              onError={handleMapError}
            />
          ) : (
            <div className="bg-brand-50 rounded-lg p-6 text-center">
              <p className="text-brand-600">
                No branch locations with coordinates available.
              </p>
              <Button 
                variant="outline" 
                className="mt-3"
                onClick={() => setViewMode('list')}
              >
                View as List
              </Button>
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <BranchList branches={branches} />
      )}
    </section>
  );
}
